import { Line, Control, Point, util as fabricUtil } from 'fabric';
import { applyHierarchicalSnap, ENDPOINT_SNAP_DISTANCE } from './snapUtils';
import { GARDEN_OBJECT_TYPES, createGardenObject } from '../../../constants/gardenObjectTypes';
import { getObjectTypeById } from '../../../config';

/**
 * LineTool - Handles line drawing logic
 *
 * Features:
 * - Draw lines by click-and-drag
 * - Snap-to-grid support for both endpoints (via snapUtils)
 * - Snap-to-endpoint support (endpoints snap to other objects' snap points) via snapUtils
 * - Ctrl: Temporarily disable grid snap (endpoint snap still active)
 * - Live preview during drawing
 * - Display length and angle
 * - Auto-switch to select mode after drawing
 */

/**
 * Calculate line properties (length and angle)
 *
 * @param {object} start - Start point {x, y}
 * @param {object} end - End point {x, y}
 * @returns {object} Properties {length, angle}
 */
export const calculateLineProperties = (start, end) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;

  // Calculate length using Pythagorean theorem
  const length = Math.sqrt(dx * dx + dy * dy);

  // Calculate angle in degrees (atan2 returns radians)
  // atan2(dy, dx) gives angle from positive x-axis, counterclockwise
  const angleRad = Math.atan2(dy, dx);
  let angle = angleRad * (180 / Math.PI);

  // Normalize angle to 0-360 range
  if (angle < 0) {
    angle += 360;
  }

  return { length, angle };
};

/**
 * Calculate line coordinates with hierarchical snap (endpoint → grid → raw)
 *
 * @param {object} start - Start point {x, y}
 * @param {object} current - Current mouse point {x, y}
 * @param {boolean} ctrlPressed - Disable grid snap (endpoint snap still active)
 * @param {number} gridSize - Grid size in pixels
 * @param {boolean} snapEnabled - Whether snap is enabled
 * @param {fabric.Canvas} canvas - Canvas instance for endpoint snapping (optional)
 * @returns {object} Line coordinates {x1, y1, x2, y2, startSnappedTo, endSnappedTo}
 */
export const calculateLineCoordinates = (
  start,
  current,
  ctrlPressed,
  gridSize,
  snapEnabled,
  canvas = null
) => {
  // Apply hierarchical snap to start point (endpoint → grid → raw)
  const startSnap = applyHierarchicalSnap(
    start,
    canvas,
    gridSize,
    snapEnabled,
    ctrlPressed,
    ENDPOINT_SNAP_DISTANCE
  );

  // Apply hierarchical snap to end point
  const endSnap = applyHierarchicalSnap(
    current,
    canvas,
    gridSize,
    snapEnabled,
    ctrlPressed,
    ENDPOINT_SNAP_DISTANCE
  );

  return {
    x1: startSnap.point.x,
    y1: startSnap.point.y,
    x2: endSnap.point.x,
    y2: endSnap.point.y,
    startSnappedTo: startSnap.snappedTo, // 'endpoint', 'grid', or 'none'
    endSnappedTo: endSnap.snappedTo,
    startSnapTarget: startSnap.target, // Fabric.js object if endpoint snap
    endSnapTarget: endSnap.target,
  };
};

/**
 * Create a Fabric.js Line object
 *
 * @param {number} x1 - Start X coordinate
 * @param {number} y1 - Start Y coordinate
 * @param {number} x2 - End X coordinate
 * @param {number} y2 - End Y coordinate
 * @param {string|null} objectTypeId - Garden object type ID (e.g., 'zaun', 'hecke')
 * @returns {Line} Fabric.js Line object with garden object metadata
 */
export const createLine = (x1, y1, x2, y2, objectTypeId = null) => {
  // Get default style from config if objectTypeId provided
  let strokeColor = '#10B981'; // Fallback to emerald-500
  let strokeWidth = 3;
  let objectConfig = null;

  if (objectTypeId) {
    objectConfig = getObjectTypeById(objectTypeId);
    if (objectConfig && objectConfig.defaultStyle) {
      const style = objectConfig.defaultStyle;
      strokeColor = style.stroke || strokeColor;
      strokeWidth = style.strokeWidth || strokeWidth;
    }
  }

  const line = new Line([x1, y1, x2, y2], {
    originX: 'left',
    originY: 'top',
    selectable: true,
    hasControls: true,
    hasBorders: false,      // No bounding box for lines
    // Custom properties for properties panel
    customType: 'line',
    // Lock scaling/rotation - we'll use custom controls for endpoints
    lockScalingX: true,
    lockScalingY: true,
    lockRotation: true,
    lockSkewingX: true,
    lockSkewingY: true,
    // Apply config colors
    stroke: strokeColor,
    strokeWidth: strokeWidth,
    // Control point styling (use stroke color for consistency)
    cornerStyle: 'circle',
    cornerColor: strokeColor,
    cornerStrokeColor: '#FFFFFF',
    cornerSize: 12,
    transparentCorners: false,
    padding: 8,             // 8px click tolerance around thin stroke
    perPixelTargetFind: true, // Pixel-accurate hit detection (not bounding box)
  });

  // Apply garden object type system (won't override colors now due to fix)
  createGardenObject(GARDEN_OBJECT_TYPES.LINE, line, {
    data: {
      shapeType: 'line', // Renamed from 'type' to avoid conflict with garden object properties
      objectTypeId: objectTypeId,
      gardenObjectType: objectTypeId, // Add both for compatibility
      createdAt: new Date().toISOString(),
      // Store original colors for selection/deselection and tab switching
      originalStroke: strokeColor,
      originalStrokeWidth: strokeWidth,
      originalCornerColor: strokeColor,
      originalOpacity: 1, // Lines are always opaque by default
      // Add config metadata if available
      ...(objectConfig && {
        isPlantable: objectConfig.isPlantable,
        castsShadow: objectConfig.castsShadow,
      }),
    },
  });

  // Completely disable all default controls
  line.setControlsVisibility({
    tl: false,
    tr: false,
    bl: false,
    br: false,
    mt: false,
    mb: false,
    ml: false,
    mr: false,
    mtr: false,
  });

  // Add custom controls at line endpoints
  // Control for start point (x1, y1)
  line.controls.p1 = new Control({
    positionHandler: function(dim, finalMatrix, fabricObject) {
      // Same approach as Fabric.js polyControl: ignore finalMatrix,
      // use vpt * objectTransform to map local coords to viewport
      const points = fabricObject.calcLinePoints();
      const matrix = fabricUtil.multiplyTransformMatrices(
        fabricObject.getViewportTransform(),
        fabricObject.calcTransformMatrix()
      );
      return new Point(points.x1, points.y1).transform(matrix);
    },
    actionHandler: function(eventData, transformData, x, y) {
      if (!transformData || !transformData.target) return false;

      const line = transformData.target;
      const canvas = line.canvas;

      // Get snap settings from canvas data (set by GardenCanvas)
      const gridSize = canvas?.gridSize || 100;
      const snapEnabled = canvas?.snapEnabled ?? true;
      const ctrlPressed = eventData?.e?.ctrlKey || false;

      // Apply hierarchical snap (endpoint → grid → raw)
      const snappedPoint = applyHierarchicalSnap(
        { x, y },
        canvas,
        gridSize,
        snapEnabled,
        ctrlPressed,
        ENDPOINT_SNAP_DISTANCE
      );

      // Use snapped coordinates
      line.set({
        x1: snappedPoint.point.x,
        y1: snappedPoint.point.y,
      });

      // Update coordinates and render
      line.setCoords();
      if (canvas) {
        canvas.requestRenderAll();
      }

      // Fire scaling event so properties panel updates in real-time
      line.fire('scaling');

      return true;
    },
    cursorStyle: 'move',
    render: function(ctx, left, top, styleOverride, fabricObject) {
      const size = this.cornerSize || fabricObject.cornerSize;
      ctx.save();
      ctx.strokeStyle = fabricObject.cornerStrokeColor;
      ctx.fillStyle = fabricObject.cornerColor;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(left, top, size / 2, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    },
  });

  // Control for end point (x2, y2)
  line.controls.p2 = new Control({
    positionHandler: function(dim, finalMatrix, fabricObject) {
      const points = fabricObject.calcLinePoints();
      const matrix = fabricUtil.multiplyTransformMatrices(
        fabricObject.getViewportTransform(),
        fabricObject.calcTransformMatrix()
      );
      return new Point(points.x2, points.y2).transform(matrix);
    },
    actionHandler: function(eventData, transformData, x, y) {
      if (!transformData || !transformData.target) return false;

      const line = transformData.target;
      const canvas = line.canvas;

      // Get snap settings from canvas data (set by GardenCanvas)
      const gridSize = canvas?.gridSize || 100;
      const snapEnabled = canvas?.snapEnabled ?? true;
      const ctrlPressed = eventData?.e?.ctrlKey || false;

      // Apply hierarchical snap (endpoint → grid → raw)
      const snappedPoint = applyHierarchicalSnap(
        { x, y },
        canvas,
        gridSize,
        snapEnabled,
        ctrlPressed,
        ENDPOINT_SNAP_DISTANCE
      );

      // Use snapped coordinates
      line.set({
        x2: snappedPoint.point.x,
        y2: snappedPoint.point.y,
      });

      // Update coordinates and render
      line.setCoords();
      if (canvas) {
        canvas.requestRenderAll();
      }

      // Fire scaling event so properties panel updates in real-time
      line.fire('scaling');

      return true;
    },
    cursorStyle: 'move',
    render: function(ctx, left, top, styleOverride, fabricObject) {
      const size = this.cornerSize || fabricObject.cornerSize;
      ctx.save();
      ctx.strokeStyle = fabricObject.cornerStrokeColor;
      ctx.fillStyle = fabricObject.cornerColor;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(left, top, size / 2, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    },
  });

  // Add fence post rendering for 'zaun' objects
  if (objectTypeId === 'zaun') {
    addFencePostRendering(line);
  }

  return line;
};

/**
 * Add fence post (Pfosten) rendering to a zaun line.
 * Overrides _render to draw small filled circles at both endpoints.
 * Posts move automatically with the line — no separate objects needed.
 *
 * @param {Line} line - Fabric.js Line object (zaun)
 */
const addFencePostRendering = (line) => {
  const originalRender = line._render.bind(line);

  line._render = function(ctx) {
    // Draw the line itself first
    originalRender(ctx);

    // Draw fence posts at both endpoints using Fabric.js' own local coords
    const postRadius = Math.max(this.strokeWidth * 0.8, 4);
    const p = this.calcLinePoints();

    ctx.save();
    ctx.fillStyle = this.stroke;
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 1.5;

    // Post at start point
    ctx.beginPath();
    ctx.arc(p.x1, p.y1, postRadius, 0, 2 * Math.PI);
    ctx.fill();
    ctx.stroke();

    // Post at end point
    ctx.beginPath();
    ctx.arc(p.x2, p.y2, postRadius, 0, 2 * Math.PI);
    ctx.fill();
    ctx.stroke();

    ctx.restore();
  };

  // Disable object caching so posts render correctly at all zoom levels
  line.set({ objectCaching: false });
};

/**
 * Restore fence post rendering on a loaded zaun line.
 * Called during canvas load to re-attach the custom _render override.
 *
 * @param {Line} line - Fabric.js Line object
 */
export const restoreFencePostRendering = (line) => {
  const objType = line.data?.gardenObjectType || line.data?.objectTypeId;
  if (objType === 'zaun') {
    addFencePostRendering(line);
  }
};

/**
 * Update line appearance based on selection state
 *
 * @param {Line} line - Fabric.js Line object
 * @param {boolean} isSelected - Whether the line is selected
 */
export const updateLineAppearance = (line, isSelected) => {
  if (!line || !line.data) return;

  if (isSelected) {
    // Selected: Use emerald-500 green
    line.set({
      stroke: '#10B981',
      cornerColor: '#10B981',
    });
  } else {
    // Deselected: Restore original config colors from object type definition
    const objectTypeId = line.data.objectTypeId || line.data.gardenObjectType;

    if (objectTypeId) {
      // Get config for this object type
      const objectConfig = getObjectTypeById(objectTypeId);

      if (objectConfig && objectConfig.defaultStyle) {
        // Restore colors from config
        line.set({
          stroke: objectConfig.defaultStyle.stroke || '#6B7280',
          strokeWidth: objectConfig.defaultStyle.strokeWidth || 3,
          cornerColor: objectConfig.defaultStyle.stroke || '#6B7280',
        });
      } else {
        // Fallback to neutral colors if no config found
        line.set({
          stroke: '#6B7280',
          strokeWidth: 3,
          cornerColor: '#6B7280',
        });
      }
    } else {
      // No object type (legacy or Zeichnen tab usage) - use neutral colors
      line.set({
        stroke: '#6B7280',
        strokeWidth: 3,
        cornerColor: '#6B7280',
      });
    }
  }

  // Store selection state in data for reference
  if (line.data) {
    line.data.isActive = isSelected;
  }
};

/**
 * Get line coordinates from Fabric.js Line object
 *
 * @param {Line} line - Fabric.js Line object
 * @returns {object} Coordinates {x1, y1, x2, y2}
 */
export const getLineCoordinates = (line) => {
  if (!line) return { x1: 0, y1: 0, x2: 0, y2: 0 };

  return {
    x1: line.x1,
    y1: line.y1,
    x2: line.x2,
    y2: line.y2,
  };
};

/**
 * Convert pixels to centimeters
 * Based on grid scale (100px = 1m = 100cm)
 *
 * @param {number} pixels - Value in pixels
 * @returns {number} Value in centimeters
 */
export const pixelsToCm = (pixels) => {
  return pixels; // 1px = 1cm in our coordinate system
};

/**
 * Convert centimeters to pixels
 *
 * @param {number} cm - Value in centimeters
 * @returns {number} Value in pixels
 */
export const cmToPixels = (cm) => {
  return cm; // 1cm = 1px in our coordinate system
};

/**
 * Convert centimeters to meters
 *
 * @param {number} cm - Value in centimeters
 * @returns {number} Value in meters
 */
export const cmToMeters = (cm) => {
  return cm / 100;
};

/**
 * Format angle for display
 * Rounds to 1 decimal place and adds degree symbol
 *
 * @param {number} angle - Angle in degrees
 * @returns {string} Formatted angle string
 */
export const formatAngle = (angle) => {
  return `${angle.toFixed(1)}°`;
};

/**
 * Format length for display
 * Converts to meters and rounds to 2 decimal places
 *
 * @param {number} lengthCm - Length in centimeters
 * @returns {string} Formatted length string
 */
export const formatLength = (lengthCm) => {
  const meters = cmToMeters(lengthCm);
  return `${meters.toFixed(2)} m`;
};
